import { recordEmailMilestone } from "../lifecycle";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { usePostHog } from "@posthog/react";
import { getStoredHostSession } from "../webAuth";
import { readPreviewReturnTo, GOOGLE_APP_ID, EXTENSION_ID } from "../hostAuth";

// The Google Picker + gapi load at runtime from Google's CDN, so there are no
// npm types — we reach them off `window` as `any`.
declare global {
  interface Window {
    gapi?: any;
    google?: any;
  }
}

// Single-purpose Google Picker host for enabling live slide previews under the
// drive.file scope. Two callers open it, and they finish differently:
//
//   CONTROLLER (?deck&magicId) — the host controller's "Enable live previews".
//     On grant we POST /preview/enable so the backend re-reads the deck now.
//
//   EXTENSION (?deck&ext) — the extension's popup, when the backend has told it
//     PREVIEW_PERMISSION_REQUIRED. It has no magicId to give us. On grant we
//     message PREVIEW_GRANTED back, and the extension re-sends START so the
//     backend retries the (now-readable) deck. It also closes this window.
//
// The extension branch exists because the extension ships on Google's review
// timeline and the clicker does not. When the grant moved into the controller
// the page started REQUIRING magicId, so every extension-initiated grant has
// been landing on "Missing details" ever since — a dead end for precisely the
// Pro hosts the backend badges. Deleting this branch is safe only once
// a Web Store build that opens the controller instead has actually shipped.
//
// Both branches take the SAME token path: the backend mints it from the host's
// stored refresh token against the Copresent session in localStorage. The old
// extension branch shuttled a token over the message channel instead; that is
// deliberately not restored, because the clicker no longer pushes a session into
// chrome.storage and the extension may well not have one.

const PICKER_API = "https://apis.google.com/js/api.js";
const API_URL = process.env.REACT_APP_API_URL ?? "";

// How long the Picker may show nothing before we record it.
//
// The Picker has no error channel: when the OAuth token is rejected it renders
// an unbranded Google 403 inside its iframe and NEVER calls setCallback. That
// failure is invisible both to this page and to the funnel — the host just sees
// a Google error and closes the window. A timer is the only instrument we have.
//
// Not proof of failure on its own: a host who reads the dialog slowly trips it
// too. That's why preview_grant_picker_result carries `silent_first` — silent
// then picked is a slow human, silent and then nothing is the 403.
const PICKER_SILENCE_MS = 15000;

type Phase = "loading" | "ready" | "granted" | "error";
type Flow = "controller" | "extension" | "unknown";

const getParam = (name: string): string =>
  new URLSearchParams(window.location.search).get(name) || "";

// Ask the extension to finish the grant on its side. Same-origin gate lives in
// the extension (externally_connectable + an origin check); we pin the id here
// so a crafted `?ext=` can't make this page talk to somebody else's extension.
const sendToExtension = (message: unknown): Promise<any> =>
  new Promise((resolve, reject) => {
    const chromeApi = (window as any).chrome;
    if (!chromeApi?.runtime?.sendMessage) {
      reject(new Error("extension_unavailable"));
      return;
    }
    chromeApi.runtime.sendMessage(EXTENSION_ID, message, (resp: any) => {
      const lastError = chromeApi.runtime.lastError;
      if (lastError) {
        reject(new Error(lastError.message || "extension_unavailable"));
        return;
      }
      resolve(resp);
    });
  });

// Where to send the host once the grant is done, for the same-tab (phone)
// flow. Sanitised in hostAuth beside the code that writes it — same-origin
// paths only.
const readReturnTo = (): string =>
  readPreviewReturnTo(window.location.search);

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("script_load_failed"));
    document.body.appendChild(script);
  });

const friendlyError = (err: unknown): string => {
  const code = err instanceof Error ? err.message : String(err);
  switch (code) {
    case "not_signed_in":
      return "Please sign in on the host controller first.";
    case "no_google_token":
      return "Your Google access expired. Sign in again on the host controller.";
    case "pro_required":
      return "Live slide previews are part of Copresent Pro.";
    case "missing_params":
      return "Missing details — reopen this from the host controller.";
    case "unknown_extension":
      return "This link didn't come from the Copresent extension.";
    case "extension_unavailable":
      return "Couldn't reach the Copresent extension. Is it still installed?";
    default:
      return "Couldn't start the file picker. Please try again.";
  }
};

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(244, 244, 246);
  padding: 24px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #24292f;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid rgba(64, 87, 109, 0.1);
  border-radius: 14px;
  padding: 28px 24px;
  max-width: 380px;
  width: 100%;
  text-align: center;
  box-shadow: 0 1px 2px rgba(64, 87, 109, 0.04),
    0 6px 18px rgba(64, 87, 109, 0.06);
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
`;

const Body = styled.p`
  margin: 0 0 18px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
`;

const Button = styled.button`
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  background: #6366f1;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease;
  &:hover {
    background: #4f46e5;
  }
`;

// The way back on a phone. The popup flow closes itself; the same-tab flow
// can't, and "you can close this window" is not an instruction a phone user can
// follow — there is no window to close, only the presentation they left.
const BackLink = styled.a`
  display: block;
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #6366f1;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const EnablePreview: React.FC = () => {
  const posthog = usePostHog();
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string>("");
  const tokenRef = useRef<string>("");
  const startedRef = useRef<boolean>(false);

  const deck = getParam("deck");
  const magicId = getParam("magicId");
  const ext = getParam("ext");
  // Set only by the same-tab (phone) flow; empty means we were popped open and
  // should close ourselves instead.
  const [returnTo] = useState<string>(readReturnTo);

  // magicId wins: the controller always sends one, and only it can drive the
  // backend render directly. `ext` alone is the extension's older shape.
  const flow: Flow = magicId ? "controller" : ext ? "extension" : "unknown";

  // Every event carries these, so the funnel can be split by caller without
  // joining across events. This page had NO instrumentation at all before, which
  // is why "hosts open the Picker and previews never turn on" could only be
  // inferred from server logs after the fact.
  const track = useCallback(
    (event: string, props?: Record<string, unknown>) => {
      if (event === "preview_grant_completed" && props?.ok === true) {
        void recordEmailMilestone("preview_enabled", deck);
      }
      posthog?.capture(event, {
        flow,
        deck_id: deck || null,
        magic_id: magicId || null,
        ...props,
      });
    },
    [posthog, flow, deck, magicId]
  );

  // Picker-silence watch — see PICKER_SILENCE_MS.
  const silenceTimer = useRef<number | null>(null);
  const sawPickerCallback = useRef(false);
  const wentSilent = useRef(false);

  const clearSilenceWatch = useCallback(() => {
    if (silenceTimer.current !== null) {
      window.clearTimeout(silenceTimer.current);
      silenceTimer.current = null;
    }
  }, []);

  const armSilenceWatch = useCallback(() => {
    sawPickerCallback.current = false;
    wentSilent.current = false;
    clearSilenceWatch();
    silenceTimer.current = window.setTimeout(() => {
      if (sawPickerCallback.current) return;
      wentSilent.current = true;
      track("preview_grant_picker_silent", {
        after_seconds: PICKER_SILENCE_MS / 1000,
      });
    }, PICKER_SILENCE_MS);
  }, [clearSilenceWatch, track]);

  useEffect(() => clearSilenceWatch, [clearSilenceWatch]);

  // Short-lived drive.file access token, minted by the backend from the host's
  // stored refresh token (the host signed in on the controller with drive.file).
  const fetchPickerToken = useCallback(async (): Promise<string> => {
    const session = getStoredHostSession();
    if (!session?.sessionToken) {
      track("preview_grant_token_result", { ok: false, error: "not_signed_in" });
      throw new Error("not_signed_in");
    }
    const resp = await fetch(`${API_URL}/preview/picker-token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${session.sessionToken}` },
    });
    if (!resp.ok) {
      let code = "no_google_token";
      try {
        const b = await resp.json();
        if (b?.error) code = b.error;
      } catch {
        /* non-JSON */
      }
      // 402 pro_required and 403 no_google_token are different problems with
      // different fixes; the status separates them even if the body doesn't.
      track("preview_grant_token_result", {
        ok: false,
        error: code,
        status: resp.status,
      });
      throw new Error(code);
    }
    const data = (await resp.json()) as { accessToken: string };
    if (!data?.accessToken) {
      track("preview_grant_token_result", { ok: false, error: "empty_token" });
      throw new Error("no_google_token");
    }
    track("preview_grant_token_result", { ok: true });
    return data.accessToken;
  }, [track]);

  // The grant is registered with Google the moment the file is picked. This is
  // the follow-up that makes previews actually appear, and it differs by caller.
  //
  // Failure here is NOT fatal — the deck is granted either way, and the next
  // slide move re-renders. But it is recorded: the old code discarded the
  // response entirely, so a 404/403 still showed the host "Live preview enabled
  // ✓" and left no trace anywhere.
  const notifyGranted = useCallback(async () => {
    if (flow === "extension") {
      // The extension re-sends START on the open Slides tab, which makes the
      // backend retry the now-readable deck. It also closes this window.
      try {
        const resp = await sendToExtension({
          type: "PREVIEW_GRANTED",
          deckId: deck,
        });
        track("preview_grant_completed", {
          ok: resp?.ok !== false,
          error: resp?.ok === false ? resp?.error ?? "extension_refused" : undefined,
        });
      } catch (err) {
        track("preview_grant_completed", {
          ok: false,
          error: err instanceof Error ? err.message : "extension_unavailable",
        });
      }
      return;
    }

    const session = getStoredHostSession();
    if (!session?.sessionToken) {
      track("preview_grant_completed", { ok: false, error: "not_signed_in" });
      return;
    }
    try {
      const resp = await fetch(
        `${API_URL}/preview/enable?magicId=${encodeURIComponent(magicId)}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${session.sessionToken}` },
        }
      );
      let code: string | undefined;
      if (!resp.ok) {
        try {
          const b = await resp.json();
          if (b?.error) code = b.error;
        } catch {
          /* non-JSON */
        }
      }
      track("preview_grant_completed", {
        ok: resp.ok,
        status: resp.status,
        error: code,
      });
    } catch {
      // Network-level failure. The grant stands; a slide move will re-render.
      track("preview_grant_completed", { ok: false, error: "network" });
    }
  }, [flow, deck, magicId, track]);

  const openPicker = useCallback(
    (token: string) => {
      const google = window.google;
      if (!google?.picker) {
        setError(friendlyError(new Error("script_load_failed")));
        setPhase("error");
        return;
      }
      const view = new google.picker.DocsView(
        google.picker.ViewId.PRESENTATIONS
      ).setFileIds(deck);

      const builder = new google.picker.PickerBuilder()
        .setOAuthToken(token)
        // OUR project number, never the URL's — see GOOGLE_APP_ID.
        .setAppId(GOOGLE_APP_ID)
        .addView(view)
        .enableFeature(google.picker.Feature.NAV_HIDDEN)
        .setCallback((data: any) => {
          const action = data[google.picker.Response.ACTION];
          // Any callback at all means the Picker is alive, so the silence watch
          // has served its purpose either way.
          sawPickerCallback.current = true;
          clearSilenceWatch();

          if (action === google.picker.Action.PICKED) {
            const docs = data[google.picker.Response.DOCUMENTS] || [];
            const pickedId = docs[0]?.[google.picker.Document.ID];
            // Belt-and-suspenders on top of the locked view.
            if (pickedId !== deck) {
              track("preview_grant_picker_result", {
                result: "wrong_file",
                silent_first: wentSilent.current,
              });
              setError(
                "That's not the deck you're presenting. Please pick the highlighted deck."
              );
              setPhase("error");
              return;
            }
            track("preview_grant_picker_result", {
              result: "picked",
              silent_first: wentSilent.current,
            });
            void notifyGranted().finally(() => {
              setPhase("granted");
              // Same tab: go back to the presentation we interrupted. Popup:
              // close, as before (the extension closes its own window). The link
              // in the granted state below is the fallback for either failing.
              window.setTimeout(() => {
                if (returnTo) window.location.replace(returnTo);
                else window.close();
              }, 1500);
            });
          } else if (action === google.picker.Action.CANCEL) {
            track("preview_grant_picker_result", {
              result: "cancelled",
              silent_first: wentSilent.current,
            });
            setPhase("ready"); // let them retry from the button
          }
        });

      builder.build().setVisible(true);
      track("preview_grant_picker_shown");
      armSilenceWatch();
    },
    [
      deck,
      notifyGranted,
      returnTo,
      track,
      armSilenceWatch,
      clearSilenceWatch,
    ]
  );

  const begin = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    try {
      // A deck is required by both callers; the controller must also name the
      // room. The extension has no magicId to give — that is the whole reason
      // this branch exists.
      if (!deck || flow === "unknown") throw new Error("missing_params");
      // Pin the extension id rather than trusting `?ext=`: on grant we message
      // it, and messaging an arbitrary extension is not something a query param
      // should be able to arrange.
      if (flow === "extension" && (!EXTENSION_ID || ext !== EXTENSION_ID)) {
        throw new Error("unknown_extension");
      }

      const token = await fetchPickerToken();
      tokenRef.current = token;

      await loadScript(PICKER_API);
      await new Promise<void>((resolve, reject) => {
        window.gapi.load("picker", {
          callback: () => resolve(),
          onerror: () => reject(new Error("script_load_failed")),
        });
      });

      setPhase("ready");
      openPicker(token);
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      track("preview_grant_error", { error: code });
      setError(friendlyError(err));
      setPhase("error");
    }
  }, [deck, ext, flow, fetchPickerToken, openPicker, track]);

  // The funnel's denominator: how many hosts reached this page at all, and from
  // where. Without it every later event is a rate with no base — the reason the
  // grant drop-off could only be reconstructed from nginx after the fact.
  // Fired once, before begin() can fail, so even missing_params is counted.
  const announcedRef = useRef(false);
  useEffect(() => {
    if (announcedRef.current) return;
    announcedRef.current = true;
    track("preview_grant_opened", {
      has_deck: !!deck,
      has_magic_id: !!magicId,
      has_ext: !!ext,
      same_tab: !!returnTo,
    });
  }, [track, deck, magicId, ext, returnTo]);

  useEffect(() => {
    void begin();
  }, [begin]);

  return (
    <Page>
      <Card>
        {phase === "loading" && (
          <>
            <Title>Enabling live preview…</Title>
            <Body>Opening the file picker for your presentation.</Body>
          </>
        )}
        {phase === "ready" && (
          <>
            <Title>Pick your deck to finish</Title>
            <Body>
              Select the highlighted deck to let your copresenters see live slide
              previews.
            </Body>
            <Button onClick={() => openPicker(tokenRef.current)}>
              Choose deck
            </Button>
            {/* An escape hatch, not a suggestion: if the Picker won't render
                here, this state is otherwise a cul-de-sac. */}
            {returnTo && (
              <BackLink href={returnTo}>Back to your presentation</BackLink>
            )}
          </>
        )}
        {phase === "granted" && (
          <>
            <Title>Live preview enabled ✓</Title>
            <Body>
              {returnTo
                ? "Taking you back to your presentation…"
                : "You can close this window and keep presenting."}
            </Body>
            {returnTo && (
              <BackLink href={returnTo}>Back to your presentation</BackLink>
            )}
          </>
        )}
        {phase === "error" && (
          <>
            <Title>Couldn't enable preview</Title>
            <Body>{error}</Body>
            <Button
              onClick={() => {
                startedRef.current = false;
                setPhase("loading");
                void begin();
              }}
            >
              Try again
            </Button>
            {/* Without this, a phone that came here same-tab and hit an error
                has no route back to the deck it was presenting. */}
            {returnTo && (
              <BackLink href={returnTo}>Back to your presentation</BackLink>
            )}
          </>
        )}
      </Card>
    </Page>
  );
};

export default EnablePreview;
